import { detachWorkflowEpisode } from '@/workflow-runner/_pure/episode';
import { episodeCommentsContext } from '@/_pure/episodeComments';
import { resolveWorkflowEpisode, workflowEpisodes } from '@/workflow-runner/episodeCatalog';
import { mode } from '@/workflow-runner/mode';
import { workflowEpisodeSupport } from './episodeSupport';
import type { EpisodePresentation } from '@/_contract/types';
import type { CosmosDataModel } from '@/workflow/_contract/cosmosData';
import type { Config } from '@/workflow-runner/_contract/config';
import type { RuntimeState } from '@/workflow-runner/_contract/runtime';

export const episodePresentation: EpisodePresentation<Config, RuntimeState, CosmosDataModel> = {
    support: workflowEpisodeSupport,
    episodes: workflowEpisodes,
    nextEpisode: (state, intent) => {
        if (intent.overlayIntentKind !== 'action-triggered' || intent.actionId !== 'episode-next') return undefined;
        const playback = state.episode;
        if (!playback) return undefined;
        const { episode, scene } = resolveWorkflowEpisode(playback.episodeHandle, state.sceneIndex ?? 0);
        if ((state.sceneIndex ?? 0) !== episode.scenes.length - 1
            || playback.beatIndex !== scene.beats.length - 1) return undefined;
        return workflowEpisodes[workflowEpisodes.indexOf(episode) + 1]?.handle;
    },
    enter: (args) => {
        const state = { ...args.runtimeState, pendingReplayMove: null,
            isPlaying: false, playbackAdvanceMode: 'manual' as const, episode: undefined };
        const next = mode.runtimeController.reduceOverlayIntent?.({ ...args, runtimeState: state,
            intent: { overlayIntentKind: 'select-changed', sectionId: 'episode-selector', value: args.handle } });
        if (!next?.episode) throw new Error(`Could not enter Runner Episode '${args.handle}'.`);
        return { ...next, episode: { ...next.episode,
            soundEnabled: args.runtimeState.episode?.soundEnabled ?? next.episode.soundEnabled } };
    },
    detach: detachWorkflowEpisode,
    playback: (state) => state.episode,
    comments: state => {
        if (!state.episode) return undefined;
        const { episode } = resolveWorkflowEpisode(state.episode.episodeHandle, state.sceneIndex ?? 0);
        return episodeCommentsContext(episode, state.sceneIndex ?? 0, state.episode.beatIndex);
    },
    inspectPlan: (state) => {
        if (!state.episode) throw new Error('No active Workflow ScenePlan.');
        const { scene } = workflowEpisodeSupport.resolveScene(state.episode.episodeHandle, state.sceneIndex ?? 0);
        return scene.sceneKind === 'trace'
            ? { schemaVersion: 1, title: scene.title, trace: scene.trace, beats: scene.beats,
                ...(scene.excerpt === undefined ? {} : { excerpt: scene.excerpt }) }
            : scene;
    },
    pause: (state) => ({ ...state, isPlaying: false, episode: state.episode
        ? { ...state.episode, isPlaying: false, narrationRequested: false } : undefined }),
    settled: (state) => state.pendingReplayMove === null,
    overlay: (model, guided) => ({ ...model, sections: model.sections.filter((section) =>
        section.sectionId !== 'episode-selector' && (!guided || section.sectionId !== 'runner-status')) }),
};
